import type { Rule } from "antd/es/form";
import { TFunction } from "i18next";
import { validateFrenchIntracom, validateSiret } from "utils/vatUtils";

// The SIRET field holds a plain string when typed, or a { label, value } object
// when set programmatically (e.g. the particular client placeholder)
const getRawValue = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  if (value && typeof value === "object" && "value" in value) {
    return String((value as { value: unknown }).value ?? "");
  }
  return "";
};

const getDigits = (value: unknown): string => getRawValue(value).replace(/\s/g, "");

/**
 * Live format checks for a French SIRET field. Wrong characters or length block the
 * submit; a wrong check digit is only a warning, so the user can still save.
 */
export function getSiretRules(t: TFunction, enabled: boolean): Rule[] {
  if (!enabled) {
    return [];
  }

  return [
    {
      validator: (_, value) => {
        const error = validateSiret(getRawValue(value));

        if (error === "notNumeric") {
          return Promise.reject(new Error(t("This field only accept numbers")));
        }
        if (error === "invalidLength") {
          return Promise.reject(
            new Error(
              t("SIRET must have 9 digits (SIREN) or 14 digits (SIRET), {{length}} entered", {
                length: getDigits(value).length,
              }),
            ),
          );
        }
        return Promise.resolve();
      },
    },
    {
      warningOnly: true,
      validator: (_, value) =>
        validateSiret(getRawValue(value)) === "invalidChecksum"
          ? Promise.reject(new Error(t("Invalid SIRET check digit, please verify the number")))
          : Promise.resolve(),
    },
  ];
}

/**
 * Live format checks for a French intracom VAT field. A wrong format blocks the submit;
 * a wrong key or a SIREN that differs from the SIRET field is only a warning.
 */
export function getFrenchIntracomRules(
  t: TFunction,
  enabled: boolean,
  siretFieldName = "vatNumber",
): Rule[] {
  if (!enabled) {
    return [];
  }

  return [
    {
      validator: (_, value) =>
        validateFrenchIntracom(getRawValue(value)) === "invalidFormat"
          ? Promise.reject(
              new Error(t("Intracom VAT must be FR + 2-character key + 9-digit SIREN")),
            )
          : Promise.resolve(),
    },
    ({ getFieldValue }) => ({
      warningOnly: true,
      validator: (_, value) => {
        const intracom = getRawValue(value);
        const error = validateFrenchIntracom(intracom);

        if (error === "invalidKey") {
          return Promise.reject(new Error(t("Invalid intracom VAT key, please verify the number")));
        }

        const siren = getDigits(getFieldValue(siretFieldName)).substring(0, 9);
        const intracomSiren = intracom.replace(/\s/g, "").substring(4, 13);

        if (!error && intracom && siren.length === 9 && siren !== intracomSiren) {
          return Promise.reject(new Error(t("Intracom VAT does not match the SIRET")));
        }
        return Promise.resolve();
      },
    }),
  ];
}
